import React, { Fragment, FunctionComponent } from 'react';
import { Grid, Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import type { SecurityRequirementObject } from '../../../utils/oasTypes';
import SecuritySchemes from '../../../utils/enums/SecuritySchemes';
import usePermissionContainerStyles from './PermissionsContainer.styles';
import { apiKeyReferenceDocLink, oauth2ReferenceDocLink } from '../../../swagger/utils/linkUtils';

interface PermissionsContainerProps {
  security: SecurityRequirementObject[];
}

const PermissionsContainer: FunctionComponent<
  React.PropsWithChildren<PermissionsContainerProps>
> = ({ security }) => {
  const { translate } = useTranslation();

  const {
    classes: { permissions: permissionsStyle, description: descriptionStyle },
  } = usePermissionContainerStyles();

  const apiKeyScopes: string[] = [];
  const oauth2Scopes: string[] = [];

  security.forEach((securityObject) => {
    if (securityObject[SecuritySchemes.APIKey]) {
      securityObject[SecuritySchemes.APIKey].forEach((scope: string) => {
        // Support reading translation keys like `Label.Read` and scopes like `asset:read`
        return apiKeyScopes.push(scope);
      });
    } else if (securityObject[SecuritySchemes.OAuth2]) {
      securityObject[SecuritySchemes.OAuth2].forEach((scope: string) => oauth2Scopes.push(scope));
    }
  });

  function renderScopesList() {
    if (apiKeyScopes.length > 0 || oauth2Scopes.length > 0) {
      const allScopes = Array.from(new Set([...apiKeyScopes, ...oauth2Scopes]));
      return (
        <ul className={permissionsStyle}>
          {allScopes.map((scope) => (
            <li key={scope}>
              <StandardMarkdownNoMargin>{scope}</StandardMarkdownNoMargin>
            </li>
          ))}
        </ul>
      );
    }
    return null;
  }

  return (
    <Grid container direction='column' wrap='nowrap'>
      {(apiKeyScopes.length > 0 || oauth2Scopes.length > 0) && (
        <Fragment>
          <Grid item XSmall>
            <Typography variant='h4' component='h4'>
              {translate('Label.RequiredPermissions')}
            </Typography>
          </Grid>

          <Grid className={descriptionStyle} item XSmall>
            <StandardMarkdownNoMargin>
              {translate('Label.RequiredScopesDescription')}
            </StandardMarkdownNoMargin>
          </Grid>

          {renderScopesList()}

          {apiKeyScopes.length > 0 && (
            <Grid className={descriptionStyle} item XSmall>
              <StandardMarkdownNoMargin>
                {translate('Label.ApiKeyReferenceDocDescription2', { apiKeyReferenceDocLink })}
              </StandardMarkdownNoMargin>
            </Grid>
          )}
          {oauth2Scopes.length > 0 && (
            <Grid className={descriptionStyle} item XSmall>
              <StandardMarkdownNoMargin>
                {translate('Label.OAuth2ReferenceDocDescription2', { oauth2ReferenceDocLink })}
              </StandardMarkdownNoMargin>
            </Grid>
          )}
        </Fragment>
      )}
    </Grid>
  );
};

export default PermissionsContainer;
